/**
 * 計画断面セグメントの判定、
 * target_date / area / section の URL パラメータ解決ロジック。
 */
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'

import { targetDateJstOffsetDays } from '~/models/dateTime'
import type { SectionSegment } from '~/pages/DemandForecastDaily/models/types'

/**
 * 計画断面 (AC-DFD-000): ワークフローの対象日と現在日付 (JST) の差で判定する。
 * 0=当日 / 1=翌日 / 2=翌々日。過去日・明後日より先は計画断面の対象外 (spec AC-DFD-050 は
 * 当日 / 翌日 / 翌々日を表示中の時だけ計画断面を出す) なので undefined。
 * targetDate 未取得 (ケース読込前) も undefined。
 */
export function resolveSectionSegmentByTargetDate(
  targetDate: { year: number; month: number; day: number } | undefined,
  now?: Date,
): SectionSegment | undefined {
  switch (targetDateJstOffsetDays(targetDate, now)) {
    case 0:
      return 'today'
    case 1:
      return 'tomorrow'
    case 2:
      return 'dayaftertomorrow'
    default:
      return undefined
  }
}

/**
 * URL の ?area=<number> パラメータ → AreaCode。
 * 不明値や未指定は undefined。
 */
export function parseAreaCodeParam(
  areaParam: string | null,
): AreaCode | undefined {
  if (areaParam === null || areaParam === '') return undefined
  const num = parseInt(areaParam, 10)
  if (isNaN(num)) return undefined
  // AreaCode enum 値に含まれるか確認
  if (Object.values(AreaCode).includes(num)) {
    return num as AreaCode
  }
  return undefined
}

/**
 * proto google.type.Date を YYYY/MM/DD 形式の文字列に変換する。
 */
export function formatProtoDate(d: ProtoDate | undefined): string {
  if (d === undefined) return '—'
  const y = d.year
  const m = String(d.month).padStart(2, '0')
  const day = String(d.day).padStart(2, '0')
  return `${String(y)}/${m}/${day}`
}

/**
 * proto google.type.Date を「M/D」形式 (非ゼロ埋め) に変換する。 KomaGrid の
 * 過去実績列ヘッダ (design 「実績 7/2」) とチャート凡例 (design L2969) の両方が
 * 同じフォーマットを使う必要があるため、1 箇所に集約した共通 formatter。
 * d が undefined の場合は undefined。
 */
export function formatProtoMonthDay(
  d: ProtoDate | undefined,
): string | undefined {
  if (d === undefined) return undefined
  return `${String(d.month)}/${String(d.day)}`
}

/** 日本語の曜日 1 文字ラベル (Date.getDay() の 0=日 〜 6=土 に対応)。 */
const DOW_LABELS_JA = ['日', '月', '火', '水', '木', '金', '土']

/**
 * proto google.type.Date の曜日を日本語 1 文字で返す (design L1771-1778 の
 * 対象日ラベル「YYYY/MM/DD (曜)」準拠)。d が undefined の場合は undefined。
 */
export function dayOfWeekLabelJa(d: ProtoDate | undefined): string | undefined {
  if (d === undefined) return undefined
  const date = new Date(d.year, d.month - 1, d.day)
  return DOW_LABELS_JA[date.getDay()]
}
